import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { loadInitialData } from '~/@app/init/loadInitialData'
import { ReqPostMessageRecord, ResPostMessageRecord } from '~/@common/constants/iframe/iframe'
import authService from '~/@common/services/auth.service'
import { envUtils } from '~/@common/utils/envUtils'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'

// 선생님앱 origin 화이트리스트 패턴
// 운영 배포: teacher.mathflat.com / teacher.schoolflat.com
// 스테이징: teacher-staging.mathflat.com / teacher-staging.schoolflat.com
// 개발: teacher-dev.mathflat.com / teacher-dev.schoolflat.com (+ mathflat.co.kr 패밀리)
const ALLOWED_TEACHER_ORIGIN_REGEX =
  /^https:\/\/teacher(-[a-z]+)?\.(mathflat\.com|mathflat\.co\.kr|schoolflat\.com)$/

// 외부 사이트가 postMessage로 LOG_OUT을 보내 강제 로그아웃시키는 걸 방지하기 위한 origin 검증
const isAllowedTeacherOrigin = (origin: string) => {
  // 로컬/프리뷰 환경은 도메인이 임의적이므로 검증 생략 (개발 편의)
  if (envUtils.isTestEnv) return true
  return ALLOWED_TEACHER_ORIGIN_REGEX.test(origin)
}

/**
 * 선생님앱 iframe에서 학생앱을 열 때 사용하는 브릿지 페이지.
 *
 * 쿼리 파라미터:
 * - studentImpersonationAuthToken: 선생님앱이 발급한 대리 로그인 토큰
 *
 * postMessage 프로토콜:
 * - PAGE_RENDERED_AFTER_LOGIN: 로그인 성공 후 홈 렌더링 완료 (Home에서 전송)
 * - BRIDGE_LOGIN_FAILED: 로그인 실패
 * - LOG_OUT (수신): 선생님앱에서 로그아웃 요청
 */
const TeacherAppBridgePage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const token = searchParams.get('studentImpersonationAuthToken')

  useEffect(() => {
    const bridgeLogin = async () => {
      // 토큰 누락 → 선생님앱(iframe 부모)에 BRIDGE_LOGIN_FAILED postMessage 전송
      if (!token) {
        window.parent.postMessage(ReqPostMessageRecord.BRIDGE_LOGIN_FAILED, '*')
        return
      }

      try {
        const response = await authService.loginByStudentImpersonationAuthToken(token)
        await loadInitialData(response.userId, response.restrictions)
        navigate(getEntrancePage('STUDENT'), { replace: true })
      } catch {
        // 로그인 API 실패 → 선생님앱(iframe 부모)에 BRIDGE_LOGIN_FAILED postMessage 전송
        window.parent.postMessage(ReqPostMessageRecord.BRIDGE_LOGIN_FAILED, '*')
      }
    }

    bridgeLogin()
  }, [token, navigate])

  // 선생님앱에서 LOG_OUT 메시지 수신 처리
  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      // 허용되지 않은 origin에서 온 LOG_OUT은 무시 (외부 사이트의 강제 로그아웃 방지)
      if (!isAllowedTeacherOrigin(e.origin)) return
      if (e.data?.type === ResPostMessageRecord.LOG_OUT.type) {
        authService.logout()
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return null
}

export default TeacherAppBridgePage
